import { Injectable, computed, signal, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, from, of, throwError } from 'rxjs';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { Preferences } from '_models/preferences';
import { map, take, catchError, switchMap } from 'rxjs/operators';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { AppCheckErrorService } from './app-check-error.service';

type Content = Record<string, any>;

@Injectable()
export class UnivPrefsService {
  private afs = inject(Firestore);
  private storage = inject(Storage);
  private sanitizer = inject(DomSanitizer);
  private appCheckErrorService = inject(AppCheckErrorService);

  // Raw preferences from Firestore (writable signal updated by getPreferences$)
  private _rawPreferences = signal<Preferences | null>(null);
  /** Population next steps from the latest calculation. Replaced on each result, not appended. */
  private readonly populationNextSteps = signal('');

  // Computed preferences with defaults applied and content sanitized
  public readonly preferences = computed(() => {
    const raw = this._rawPreferences();
    if (!raw) return null;

    // Apply defaults
    const withDefaults = this.applyDefaults(raw);

    // Sanitize content
    return this.sanitizeContent(withDefaults);
  });

  // Computed for derived state
  public readonly isLoaded = computed(() => this._rawPreferences() !== null);
  public readonly content = computed((): Content => {
    const prefs = this.preferences();
    if (!prefs?.content) return {};
    return prefs.content as Content;
  });
  public readonly nextSteps = computed(() => {
    const combined = this.combinedNextSteps();
    return combined ? this.sanitizer.bypassSecurityTrustHtml(combined) : '';
  });

  /** Unsanitized HTML string for PDF export (SafeHtml cannot be stringified). */
  public readonly nextStepsHtml = computed(() => this.combinedNextSteps());

  /** True when a content HTML field has visible text (ignores empty Quill markup). */
  hasContentHtml(key: string): boolean {
    const raw = this._rawPreferences()?.content as Content | undefined;
    const value = raw?.[key];
    return typeof value === 'string' && this.htmlHasVisibleText(value);
  }

  // Convert preferences signal to Observable for image loading
  private preferences$ = toObservable(this.preferences);

  // Image URL signals - automatically reload when preferences change
  public readonly logoURL = toSignal(
    this.preferences$.pipe(
      switchMap(prefs => {
        if (!prefs?.logoPath) return of('');
        return from(getDownloadURL(ref(this.storage, prefs.logoPath))).pipe(
          catchError(() => of(''))
        );
      })
    ),
    { initialValue: '' }
  );

  public readonly headerBgURL = toSignal(
    this.preferences$.pipe(
      switchMap(prefs => {
        if (!prefs) return of('');

        switch (prefs.headerType) {
          case 'hSolid':
            return of('');
          case 'hGradient':
            return of(prefs.headerGradient || '');
          case 'hImage':
            if (prefs.headerBgImagePath) {
              return from(getDownloadURL(ref(this.storage, prefs.headerBgImagePath))).pipe(
                map(url => `url(${url})`),
                catchError(() => of(''))
              );
            }
            return of('');
          default:
            return of('');
        }
      })
    ),
    { initialValue: '' }
  );

  public readonly pageBgURL = toSignal(
    this.preferences$.pipe(
      switchMap(prefs => {
        if (!prefs) return of('');

        switch (prefs.backgroundType) {
          case 'bgSolid':
            // Update document.body.style.background
            if (prefs.backgroundColor) {
              document.body.style.background = prefs.backgroundColor;
            }
            return of('');
          case 'bgGradient':
            if (prefs.backgroundGradient) {
              document.body.style.backgroundImage = prefs.backgroundGradient;
            }
            return of(prefs.backgroundGradient || '');
          case 'bgImage':
            if (prefs.pageBgImagePath) {
              return from(getDownloadURL(ref(this.storage, prefs.pageBgImagePath))).pipe(
                map(url => {
                  document.body.style.backgroundImage = `url(${url})`;
                  return `url(${url})`;
                }),
                catchError(() => of(''))
              );
            }
            return of('');
          default:
            return of('');
        }
      })
    ),
    { initialValue: '' }
  );

  hasCustomQuestions = false;

  public getPreferences$(schoolId: string, useDraft = false): Observable<boolean | undefined> {
    const path = useDraft ? 'preferences_draft/' : 'preferences/';
    const docRef = doc(this.afs, path + schoolId);

    return docData(docRef).pipe(
      take(useDraft ? Infinity : 1),
      map(prefs => {
        if (prefs) {
          // Update raw preferences signal - computed signals will automatically update
          this._rawPreferences.set(prefs as Preferences);
          return true;
        }
        this._rawPreferences.set(null);
        return false;
      }),
      catchError(error => {
        this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
          operation: 'getPreferences',
          schoolId,
          assumeAppCheck: true
        });
        return throwError(() => error);
      })
    );
  }


  public addNextSteps(populationSteps: string) {
    this.populationNextSteps.set(populationSteps || '');
  }

  private schoolNextSteps(): string {
    const raw = this._rawPreferences()?.content as Content | undefined;
    const value = raw?.['nextSteps'];
    return typeof value === 'string' ? value : '';
  }

  private combinedNextSteps(): string {
    const combined = this.schoolNextSteps() + this.populationNextSteps();
    return this.htmlHasVisibleText(combined) ? combined : '';
  }

  /** Empty Quill documents (`<p><br></p>`, `&nbsp;`) have no visible text. */
  private htmlHasVisibleText(value: string): boolean {
    const text = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 0;
  }

  private applyDefaults(preferences: Preferences): Preferences {
    const defaults: Partial<Preferences> = {
      showEFC: true,
      panelColor: '#FFFFFF',
      headerShrink: false,
      stateGrant: 'State Grant',
      institutionalGrant: 'Institutional Grant',
      institutionalScholarship: 'Institutional Scholarship',
      consolidatedSelfHelp: 'Consolidated Self Help',
      schoolDefinedFund1: 'School Defined Fund 1',
      schoolDefinedFund2: 'School Defined Fund 2',
      welcomeImageClass: {}
    };

    // Remove empty strings and merge defaults
    const cleaned: any = { ...preferences };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === '') {
        delete cleaned[key];
      }
    });

    return { ...defaults, ...cleaned } as Preferences;
  }

  private sanitizeContent(prefs: Preferences): Preferences {
    if (!prefs.content) return prefs;

    const sanitizedContent: Content = {};
    const rawContent = prefs.content as Content;
    for (const c in rawContent) {
      if (c) {
        sanitizedContent[c] = rawContent[c]
          ? this.sanitizer.bypassSecurityTrustHtml(rawContent[c] as string)
          : '';
      }
    }

    return {
      ...prefs,
      content: sanitizedContent
    };
  }

}
